/*
 * Magic Afro — cart "Découvrez aussi" carousel behaviour.
 *
 * Cards are rendered server-side (snippets/magic-cart-recommendations.liquid)
 * with the same curated products as live magicafro.fr. This script wires
 * the dot navigation + "Ajouter" buttons.
 *
 * Robustness: the cart drawer morphs its content on every cart change,
 * which can wipe the dots (the <magic-cart-recos> element persists, so
 * connectedCallback doesn't re-fire). So we (re)build on BOTH the
 * element's connect AND the theme's `cart:update` event, and the render
 * is idempotent. Registered once from layout/theme.liquid (inline
 * scripts inside the morphed drawer never execute).
 */
(function () {
  const SCROLL_BOUND = new WeakSet();
  const CLICK_BOUND = new WeakSet();

  function render(el) {
    const track = el.querySelector('.magic-cart-recos__track');
    const dotsWrap = el.querySelector('.magic-cart-recos__dots');
    const viewport = el.querySelector('.magic-cart-recos__viewport');
    if (!track || !dotsWrap || !viewport) return;

    const cards = track.querySelectorAll('.magic-cart-recos__card');
    if (!cards.length) { el.style.display = 'none'; return; }
    el.style.display = '';

    // (Re)build dots to match the current card count.
    if (dotsWrap.children.length !== cards.length) {
      dotsWrap.innerHTML = '';
      if (cards.length > 1) {
        for (let i = 0; i < cards.length; i++) {
          const d = document.createElement('button');
          d.type = 'button';
          d.className = 'magic-cart-recos__dot' + (i === 0 ? ' is-active' : '');
          d.setAttribute('aria-label', 'Produit ' + (i + 1));
          d.addEventListener('click', () => {
            viewport.scrollTo({ left: cards[i].offsetLeft, behavior: 'smooth' });
          });
          dotsWrap.appendChild(d);
        }
      }
    }

    // Sync active dot on scroll (attach once per viewport).
    if (!SCROLL_BOUND.has(viewport)) {
      SCROLL_BOUND.add(viewport);
      const syncDots = () => {
        const cs = track.querySelectorAll('.magic-cart-recos__card');
        let active = 0;
        let min = Infinity;
        for (let i = 0; i < cs.length; i++) {
          const delta = Math.abs(cs[i].offsetLeft - viewport.scrollLeft);
          if (delta < min) { min = delta; active = i; }
        }
        Array.from(dotsWrap.children).forEach((d, i) => d.classList.toggle('is-active', i === active));
      };
      viewport.addEventListener('scroll', () => window.requestAnimationFrame(syncDots));
    }

    // "Ajouter" → add variant, then notify the theme to refresh the drawer.
    if (!CLICK_BOUND.has(el)) {
      CLICK_BOUND.add(el);
      el.addEventListener('click', async (e) => {
        const btn = e.target.closest && e.target.closest('.magic-cart-recos__add');
        if (!btn || btn.disabled) return;
        const variantId = btn.dataset.variantId;
        if (!variantId) return;
        btn.disabled = true;
        btn.textContent = 'Ajout…';
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
          });
          btn.textContent = '✓ Ajouté';
          // Carry itemCount + resource: cart-icon.js SETS the bubble to
          // detail.data.itemCount ?? 0, so an itemCount-less dispatch used
          // to hide the badge after a recos add.
          let cart = null;
          try {
            cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
          } catch (_) {}
          // Dispatch only with a real snapshot — an itemCount-less event
          // makes cart-icon.js coerce `?? 0` and hide the badge after a
          // successful add (pre-launch review 2026-07-23).
          if (cart) {
            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: cart,
                  sourceId: 'magic-cart-recos',
                  data: {
                    source: 'magic-cart-recos',
                    variantId: String(variantId),
                    itemCount: cart.item_count,
                  },
                },
              })
            );
          }
        } catch (err) {
          btn.textContent = 'Ajouter';
          btn.disabled = false;
        }
      });
    }
  }

  function renderAll() {
    document.querySelectorAll('magic-cart-recos').forEach(render);
  }

  if (!customElements.get('magic-cart-recos')) {
    customElements.define(
      'magic-cart-recos',
      class extends HTMLElement {
        connectedCallback() {
          render(this);
        }
      }
    );
  }

  // Reliable trigger: the cart drawer clones its content from a
  // <template> on open and morphs it on change — neither reliably fires
  // connectedCallback. A debounced MutationObserver catches every case
  // (appear / open / morph). render() is idempotent (skips when dots
  // already match the card count) so this never loops.
  if (!window.__magicRecosCartBound) {
    window.__magicRecosCartBound = true;

    let scheduled = false;
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        // Only act if some recos needs (re)building.
        const els = document.querySelectorAll('magic-cart-recos');
        for (const el of els) {
          const track = el.querySelector('.magic-cart-recos__track');
          const dots = el.querySelector('.magic-cart-recos__dots');
          if (!track || !dots) continue;
          const cards = track.querySelectorAll('.magic-cart-recos__card').length;
          if (cards && dots.children.length !== cards) { render(el); }
        }
      });
    };

    // Scope the observer to the cart drawer(s) when present (cheaper than
    // watching the whole document); fall back to body only if the drawer
    // isn't in the DOM yet. The rAF debounce + the cards/dots gate above
    // keep the callback work near-zero, so there's no perf cost.
    const observer = new MutationObserver(schedule);
    const start = () => {
      const targets = document.querySelectorAll('cart-drawer-component');
      if (targets.length) {
        targets.forEach((t) => observer.observe(t, { childList: true, subtree: true }));
      } else if (document.body) {
        observer.observe(document.body, { childList: true, subtree: true });
      }
    };
    if (document.body) start();
    else document.addEventListener('DOMContentLoaded', start);

    // cart:update fires on every cart change; re-render after the morph.
    document.addEventListener('cart:update', () => {
      setTimeout(renderAll, 60);
      setTimeout(renderAll, 350);
    });
    document.addEventListener('DOMContentLoaded', renderAll);
  }
})();
